import React, { useEffect, useRef, useState } from 'react';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Desktop nav (phones use the top bar's menu). It collapses to a rail of
// icons, with each link's name in a tooltip; the choice is kept per browser.

const PREF = 'sidebar.collapsed';
// Matches the width transition in index.css
const TRANSITION_MS = 200;

const readCollapsed = () => {
  try {
    return localStorage.getItem(PREF) === '1';
  } catch (e) {
    return false;
  }
};

// Icon + name, or just the icon with the name in a tooltip when collapsed
const SideLink = ({ to, icon, label, active, collapsed, title }) => {
  const link = (
    <Nav.Link
      as={Link}
      to={to}
      className={active ? 'active' : ''}
      title={collapsed ? undefined : title}
      aria-label={collapsed ? label : undefined}
    >
      <i className={icon}></i>
      <span className="sidebar-label">{label}</span>
    </Nav.Link>
  );
  if (!collapsed) return link;
  return (
    <OverlayTrigger
      placement="right"
      delay={{ show: 100, hide: 0 }}
      overlay={<Tooltip id={`sidebar-tip-${to}`}>{title || label}</Tooltip>}
    >
      {link}
    </OverlayTrigger>
  );
};

const Sidebar = () => {
  const { user, isAdmin, canManagePages, canAccessAccounting, canAccessQuickBooks, canAccessDocSign, canAccessApplications, canAccessWaterUsage, canAccessInsurance, canAccessCameras, canAccessTraffic, canAccessDropbox, canManagePortalDocs, canAccessDriverTablets, canAccessDoors, canAccessFuel } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => setCollapsed((was) => {
    try {
      localStorage.setItem(PREF, was ? '0' : '1');
    } catch (e) { /* private mode */ }
    return !was;
  });

  // Pages that fit themselves to the window measure on resize; the content
  // area just changed width without one
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return undefined;
    }
    const id = setTimeout(() => window.dispatchEvent(new Event('resize')), TRANSITION_MS + 20);
    return () => clearTimeout(id);
  }, [collapsed]);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const item = (to, icon, label) => (
    <SideLink to={to} icon={icon} label={label} active={isActive(to)} collapsed={collapsed} />
  );

  // For mobile screens, we'll hide the sidebar and rely on the navbar
  return (
    <div className={`sidebar d-none d-lg-flex${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-inner">
        <Nav className="flex-column sidebar-nav">
          {item('/dashboard', 'bi bi-house', 'Dashboard')}
          {item('/pages', 'bi bi-file-earmark-text', 'Pages')}
          {item('/incidents', 'bi bi-exclamation-triangle', 'Incidents')}
          {item('/passwords', 'bi bi-key-fill', 'Passwords')}
          {item('/notes', 'bi bi-file-text', 'Notes')}
          {item('/employees', 'bi bi-people-fill', 'Employees')}
          {canAccessAccounting() && item('/accounting', 'bi bi-calculator', 'Accounting')}
          {canAccessQuickBooks() && item('/quickbooks', 'bi bi-receipt-cutoff', 'QuickBooks')}
          {canAccessFuel() && item('/fuel', 'bi bi-fuel-pump', 'Fuel')}
          {canAccessDocSign() && item('/docsign', 'bi bi-pen', 'DocSign')}
          {canAccessApplications() && item('/applications', 'bi bi-person-vcard', 'Applications')}
          {canAccessWaterUsage() && item('/water-usage', 'bi bi-droplet-half', 'Water Usage')}
          {canAccessInsurance() && item('/insurance', 'bi bi-shield-check', 'Insurance')}
          {canAccessDropbox() && item('/dropbox', 'bi bi-cloud-arrow-up', 'Dropbox')}
          {canManagePortalDocs() && item('/driver-docs', 'bi bi-folder2-open', 'Driver Docs')}
          {canAccessCameras() && item('/cameras', 'bi bi-camera-video', 'Cameras')}
          {canAccessTraffic() && item('/traffic', 'bi bi-activity', 'Traffic')}
          {canAccessDriverTablets() && item('/driver-tablets', 'bi bi-truck', 'Driver Tablets')}
          {canAccessDoors() && item('/doors', 'bi bi-door-open', 'Doors')}
          {item('/plugins', 'bi bi-puzzle', 'Plugins')}

          {/* Custom Tag Filters */}
          {user?.customTagFilters && user.customTagFilters.length > 0 && (
            <>
              <hr className="sidebar-divider" />
              <div className="sidebar-heading">Quick Filters</div>

              {user.customTagFilters.map((filter, index) => {
                // Handle both old (single tag) and new (multiple tags) format for backwards compatibility
                const tags = filter.tags || (filter.tag ? [filter.tag] : []);
                const tagQuery = tags.map(tag => `tag=${encodeURIComponent(tag)}`).join('&');

                // Check if any of the filter's tags are currently active in the URL
                const urlParams = new URLSearchParams(location.search);
                const activeTags = urlParams.getAll('tag');
                const isFilterActive = location.pathname === '/pages' &&
                  tags.some(tag => activeTags.includes(tag));

                return (
                  <SideLink
                    key={index}
                    to={`/pages?${tagQuery}`}
                    icon={filter.icon}
                    label={filter.label}
                    active={isFilterActive}
                    collapsed={collapsed}
                    title={`Filter pages by tags: ${tags.join(', ')}`}
                  />
                );
              })}
            </>
          )}

          {(isAdmin || canManagePages()) && (
            <>
              <hr className="sidebar-divider" />
              <div className="sidebar-heading">
                {isAdmin ? 'Administration' : 'Management'}
              </div>

              {isAdmin && (
                <>
                  {item('/admin/dashboard', 'bi bi-speedometer2', 'Admin Dashboard')}
                  {item('/admin/users', 'bi bi-people', 'Manage Users')}
                  {item('/admin/reports', 'bi bi-envelope-paper', 'Scheduled Reports')}
                  {item('/admin/inbound-emails', 'bi bi-inbox', 'Inbound Mail')}
                </>
              )}

              {canManagePages() && item('/admin/pages', 'bi bi-file-earmark-plus', 'Manage Pages')}
            </>
          )}

          <hr className="sidebar-divider" />
          <div className="sidebar-heading">Account</div>

          {item('/profile', 'bi bi-person', 'Profile')}
          {item('/change-password', 'bi bi-key', 'Change Password')}
        </Nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div className="sidebar-user">
              <div className="sidebar-user-name">{user?.username}</div>
              <div className="sidebar-user-role">{user?.role}</div>
            </div>
          )}
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar to icons'}
            title={collapsed ? `Expand the sidebar (${user?.username || 'signed in'})` : 'Collapse to icons'}
          >
            <i className={`bi bi-chevron-double-${collapsed ? 'right' : 'left'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
